// C:\Helpdesk\frontend\src\pages\admin\Organization.js
import React, { useState, useEffect, useCallback } from 'react';
// Removed 'message' import, now using global notification system
import { Table, Button, Space, Tag, Popconfirm, Input, Select, Badge, Modal } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import OrganizationForm from './OrganizationForm';
import api from '../../utils/api';
import './users.css'; // Keep this for now, but consider renaming/refactoring to organizations.css
import dayjs from 'dayjs';
import { useAppContext } from '../../context/AppContext';
import { useNotificationApi } from '../../context/NotificationContext'; // <--- NEW IMPORT

const { Search } = Input;
const { Option } = Select;

const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingOrganization, setEditingOrganization] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('all');
  // --- MODIFIED: Default filterStatus to 'active' ---
  const [filterStatus, setFilterStatus] = useState('active'); // Changed default from 'all' to 'active'
  const [deactivatingId, setDeactivatingId] = useState(null); // State for loading on Popconfirm button
  const [activatingId, setActivatingId] = useState(null); // State for loading on Popconfirm button

  const { appSettings } = useAppContext();
  const notificationApi = useNotificationApi(); // <--- NEW: Get notification API from context

  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  // Helper function to show errors (persistent notification, top-right)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  // Helper function to show success (transient notification, top-right)
  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  // Helper function to show loading (sticky notification, top-right, with delay and min display time)
  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchOrganizations = useCallback(async (triggeredByAction = false) => {
    setLoading(true);
    const loadingKey = 'fetchOrgsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading organizations...');

    try {
      const params = {};
      // --- MODIFIED: Pass filterStatus and filterType directly to API call ---
      if (filterStatus !== 'all') {
        params.status = filterStatus; // Backend expects 'active' or 'inactive'
      } else {
        // If 'all' status is selected, ensure no status param is sent to fetch all
        delete params.status;
      }
      if (filterType !== 'all') {
        params.type = filterType; // Backend expects 'partner', 'customer', 'internal'
      } else {
        // If 'all' type is selected, ensure no type param is sent to fetch all
        delete params.type;
      }
      if (searchText) {
        params.search = searchText;
      } else {
        delete params.search;
      }

      const response = await api.get('/api/organizations', { params });
      let fetchedOrgs = response.data.data || [];

      if (!Array.isArray(fetchedOrgs)) {
        console.warn('Organizations.js: API response for organizations was not an array or expected object format. Assuming empty array.');
        fetchedOrgs = [];
      }
      setOrganizations(fetchedOrgs);
      hideLoading();

      if (!triggeredByAction) {
        showSuccess('Organizations Loaded', 'Organization list fetched successfully!', 'fetchOrgsSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching organizations:', error.response?.data || error.message, error);
      showError('Failed to Fetch Organizations', error.response?.data?.message || 'An unexpected error occurred while fetching organizations.', 'fetchOrgsError');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchText, filterType, showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations, dateFormat, timeFormat]);

  const handleAddOrganization = () => {
    setEditingOrganization(null);
    setIsModalVisible(true);
  };

  const handleEditOrganization = (organizationRecord) => {
    setEditingOrganization(organizationRecord);
    setIsModalVisible(true);
  };

  const handleDeactivateOrganization = async (orgId, orgName) => {
    setDeactivatingId(orgId);
    const loadingKey = `orgStatusUpdate-${orgId}`;
    const hideLoading = showLoading(loadingKey, `Deactivating ${orgName}...`);

    try {
      await api.patch(`/api/organizations/${orgId}/deactivate`);
      hideLoading();
      showSuccess('Organization Status Updated', `${orgName} was deactivated.`, 'orgStatusUpdateSuccess');
      fetchOrganizations(true);
    } catch (error) {
      hideLoading();
      console.error('Error deactivating organization:', error.response?.data || error.message, error);
      showError('Cannot Deactivate Organization', error.response?.data?.message || `Failed to deactivate ${orgName}.`, `deactivateOrgError-${orgId}`);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleActivateOrganization = async (orgId, orgName) => {
    setActivatingId(orgId);
    const loadingKey = `orgStatusUpdate-${orgId}`;
    const hideLoading = showLoading(loadingKey, `Activating ${orgName}...`);

    try {
      await api.patch(`/api/organizations/${orgId}/activate`);
      hideLoading();
      showSuccess('Organization Status Updated', `${orgName} was activated.`, 'orgStatusUpdateSuccess');
      fetchOrganizations(true);
    } catch (error) {
      hideLoading();
      console.error('Error activating organization:', error.response?.data || error.message, error);
      showError('Cannot Activate Organization', error.response?.data?.message || `Failed to activate ${orgName}.`, `activateOrgError-${orgId}`);
    } finally {
      setActivatingId(null);
    }
  };

  // --- MODIFIED: Removed client-side filtering as API handles it via filterStatus and filterType ---
  // The fetchOrganizations now uses filterStatus, filterType, and searchText directly in the API call,
  // so client-side filtering based on these is redundant.
  // const filteredOrganizations = organizations.filter(org => {
  //   const orgName = org.name || '';
  //   const orgEmail = org.email || '';
  //   const orgPhone = org.phoneNumber || '';
  //   const orgFax = org.faxNumber || '';
  //   const orgAddress = org.address || '';
  //   const orgWebsite = org.websiteUrl || '';

  //   const matchesSearch = orgName.toLowerCase().includes(searchText.toLowerCase()) ||
  //                         orgEmail.toLowerCase().includes(searchText.toLowerCase()) ||
  //                         orgPhone.toLowerCase().includes(searchText.toLowerCase()) ||
  //                         orgFax.toLowerCase().includes(searchText.toLowerCase()) ||
  //                         orgAddress.toLowerCase().includes(searchText.toLowerCase()) ||
  //                         orgWebsite.toLowerCase().includes(searchText.toLowerCase());

  //   const matchesType = filterType === 'all' || org.type === filterType;

  //   const matchesStatus = filterStatus === 'all' ||
  //                         (filterStatus === 'active' && org.isActive) ||
  //                         (filterStatus === 'inactive' && !org.isActive);

  //   return matchesSearch && matchesType && matchesStatus;
  // });

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (text, record) => ( // Make name clickable for editing
        <a onClick={() => handleEditOrganization(record)}>{text}</a>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'customer' ? 'geekblue' : type === 'partner' ? 'purple' : 'default'}>
          {type ? type.toUpperCase() : 'N/A'}
        </Tag>
      ),
      // --- MODIFIED: Removed Ant Design table's built-in filters ---
      // These filters are redundant since we are now using the Select dropdown
      // for filtering type, and the API handles it.
      // filters: [
      //   { text: 'Customer', value: 'customer' },
      //   { text: 'Partner', value: 'partner' },
      // ],
      // onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
      render: (email) => email || 'N/A',
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber', // Expecting camelCase from backend mapping
      key: 'phoneNumber',
      render: (number) => number || 'N/A',
    },
    {
      title: 'Fax Number',
      dataIndex: 'faxNumber', // Expecting camelCase from backend mapping
      key: 'faxNumber',
      render: (number) => number || 'N/A',
    },
    {
        title: 'Address',
        dataIndex: 'address',
        key: 'address',
        render: (address) => address || 'N/A',
    },
    {
        title: 'Website',
        dataIndex: 'websiteUrl', // Expecting camelCase from backend mapping
        key: 'websiteUrl',
        render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">{url}</a> : 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'isActive', // Expecting camelCase from backend mapping
      key: 'isActive',
      render: (isActive) => ( // Parameter name also changed to isActive
        <Badge
          status={isActive ? 'success' : 'error'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
      // --- MODIFIED: Removed Ant Design table's built-in filters ---
      // These filters are redundant since we are now using the Select dropdown
      // for filtering status, and the API handles it.
      // filters: [
      //   { text: 'Active', value: 'active' },
      //   { text: 'Inactive', value: 'inactive' },
      // ],
      // onFilter: (value, record) => (value === 'active' && record.isActive) || (value === 'inactive' && !record.isActive), // Using record.isActive
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt', // Expecting camelCase from backend mapping
      key: 'createdAt',
      render: (date) => date ? dayjs(date).format(`${dateFormat} ${timeFormat}`) : 'N/A',
      sorter: (a, b) => {
        // Using a.createdAt and b.createdAt for sorting as per backend mapping
        const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return dateA - dateB;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle"> {/* Changed to small size for action buttons consistency */}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditOrganization(record)}
            aria-label={`Edit ${record.name}`} // Added aria-label
          />
          {record.isActive ? ( // Using record.isActive
            <Popconfirm
              title={`Are you sure you want to deactivate ${record.name}?`}
              onConfirm={() => handleDeactivateOrganization(record.id, record.name)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: deactivatingId === record.id, danger: true }}
            >
              <Button type="link" danger icon={<CloseCircleOutlined />} aria-label={`Deactivate ${record.name}`} /> {/* Added aria-label */}
            </Popconfirm>
          ) : (
            <Popconfirm
              title={`Are you sure you want to activate ${record.name}?`}
              onConfirm={() => handleActivateOrganization(record.id, record.name)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: activatingId === record.id }}
            >
              <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Activate ${record.name}`} /> {/* Added aria-label */}
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="organizations-page">
      <div className="organizations-header">
        <h2>Organization Management</h2>
        <div className="organizations-actions">
          {/* --- MODIFIED: Use Ant Design Space for consistent horizontal spacing --- */}
          <Space size="middle">
            <Search
              placeholder="Search organizations..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText} // Bind value to state
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => fetchOrganizations(true)} // Trigger fetch on search
              style={{ width: 300 }}
            />
            <Select
              defaultValue="all"
              size="large"
              style={{ width: 150 }}
              value={filterType} // Bind value to state
              onChange={(value) => {
                setFilterType(value); // Update state
                fetchOrganizations(true); // Trigger fetch with new type
              }}
            >
              <Option value="all">All Types</Option>
              <Option value="customer">Customer</Option>
              <Option value="partner">Partner</Option>
            </Select>
            <Select
              // --- MODIFIED: Default value to 'active' and bind value to state ---
              // --- MODIFIED: Reordered Options to Active, Inactive, All Status ---
              defaultValue="active" // Changed default from 'all' to 'active'
              size="large"
              style={{ width: 150 }}
              value={filterStatus} // Bind value to state
              onChange={(value) => {
                setFilterStatus(value); // Update state
                fetchOrganizations(true); // Trigger fetch with new status
              }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="all">All Status</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddOrganization}
              size="large"
            >
              Add Organization
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={organizations} // MODIFIED: Use 'organizations' directly as dataSource as API filters
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <OrganizationForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={fetchOrganizations}
        organization={editingOrganization}
      />
    </div>
  );
};

export default Organizations;